import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import './App.css'
import { Results as MediapipeResults } from '@mediapipe/pose'
import { Results as MediapipeHandsResults } from '@mediapipe/hands'
import { LineChart, Line } from 'recharts'
import MediapipeProcessor from './utils/mediapipeProcessor'
// import { Results as MediapipeResults } from '@mediapipe/holistic'
// import MediapipeProcessor from './utils/mediapipeProcessorHolistic'
import { useCamera } from './hooks/useCamera'
import { useOrientation } from './hooks/useOrientation'
import { poseFilenameToVideoPath } from './utils/video'
import { VideoDisplay } from './components/VideoDisplay'
import { ExerciseCanvas } from './components/ExerciseCanvas'
import { StatusDisplay } from './components/StatusDisplay'
import { PoseCanvas } from './components/PoseCanvas'
import { PoseCanvas3D } from './components/PoseCanvas3D'
import { ExerciseProvider, useExerciseContext } from './context/ExerciseContext'
import { blazeMapToPose, Motion, Pose } from './utils/pose'
import { RepCounter } from './utils/repCounter'
import { SettingsProvider, useSettings } from './context/SettingsContext'
import { SettingsArea } from './components/SettingsArea'
import { JointBars } from './components/JointBars'
import { ExerciseBars } from './components/ExerciseBars'

const buttonStyle = {
  backgroundColor: 'orange',
  color: 'white',
  border: '2px solid #555',
  borderRadius: '28px',
  width: '38px',
  height: '38px',
  cursor: 'pointer',
  fontSize: '20px',
}

// Component that uses exercise context
const AppContent: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const { settings } = useSettings()

  const { getCurrentExercise, setSelectedExerciseName, availableExercises } = useExerciseContext()
  const currentExercise = getCurrentExercise()

  // Replay mode state
  const [isReplayMode, setIsReplayMode] = useState<boolean>(false)
  const [videoSrc, setVideoSrc] = useState<MediaStream | string | null>(null)

  // defines the desired camera resolution based on orientation
  const resolution = useOrientation({ width: settings.preferredResolution?.width || 640, height: settings.preferredResolution?.height || 480 })

  const camera = useCamera()
  // const [mpRes, setMpRes] = useState<string>('')

  const repCounter = useMemo<RepCounter | null>(() => currentExercise?.keyposes?.length ? new RepCounter(currentExercise.keyposes.length) : null, [currentExercise])
  const motion = useMemo<Motion | null>(() => new Motion(), [])

  const [stateChartData, setStateChartData] = useState<any[]>([])
  const [isOutOfFrame, setIsOutOfFrame] = useState<boolean>(false)

  // Memoize the current keypose index to prevent unnecessary re-renders
  const currentKeyposeIndex = useMemo(() => {
    return stateChartData?.[0]?.state || 0
  }, [stateChartData])

  // State for video element references
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null)

  const [poseData, setPoseData] = useState<Pose | null>(null)
  const [handsResults, setHandsResults] = useState<MediapipeHandsResults | null>(null)

  const isMediaPipeActive = (isReplayMode || camera.isCameraRunning) && !!videoElement

  const frameCountRef = useRef<number>(0)
  const lastTimeRef = useRef<number>(performance.now())
  const [fps, setFps] = useState<number>(0)

  const updateFps = () => {
    frameCountRef.current++
    const currentTime = performance.now()
    const deltaTime = currentTime - lastTimeRef.current

    if (deltaTime >= 1000) { // Update FPS every second
      const currentFps = Math.round((frameCountRef.current * 1000) / deltaTime)
      setFps(currentFps)
      frameCountRef.current = 0
      lastTimeRef.current = currentTime
    }
  }

  const repCount = repCounter?.getCount() || 0
  useEffect(() => {
    if (repCount >= 10) {
      setSelectedExerciseName(availableExercises[Math.floor(Math.random() * availableExercises.length)])
    }
  }, [repCount, setSelectedExerciseName, availableExercises])

  const bestScoreThreshold = 0.8

  const onPoseResults = useCallback((results: MediapipeResults, handsResults: MediapipeHandsResults | null) => {
    // Calculate FPS
    updateFps()

    if (handsResults?.multiHandWorldLandmarks) {
      // document.title = handsResults.multiHandWorldLandmarks.length.toString()
      setHandsResults(handsResults)
    }

    // MediapipeProcessor.mediapipeInstance.setOptions({
    //   smoothLandmarks: true,
    //   // minDetectionConfidence: 0.8,
    //   // minTrackingConfidence: 0.2,
    // })
    // TODO: checkbox for processing joints angles?
    if (results?.poseLandmarks) {
      // setMpRes(results.image.width + 'x' + results.image.height)
      // console.log(results.image.width + 'x' + results.image.height)

      const poseData = blazeMapToPose(results)
      // setPoseData(poseData)

      if (currentExercise && poseData && motion) {
        motion.update(poseData)
        const smoothPose = motion.calcSmoothPose(-1, 3)
        setPoseData(smoothPose)

        const missingLandmarks = currentExercise.getMissingLandmarks(poseData, bestScoreThreshold)
        if (missingLandmarks.length > 2) {
          // console.log('Missing landmarks:', LandmarkName[missingLandmarks[0]])
          setIsOutOfFrame(true)
          // return
        } else {
          setIsOutOfFrame(false)
        }

        const state = currentExercise.getState(poseData)
        if (!state.valid || state.score < bestScoreThreshold) {
          // console.log('Invalid state:', state.index)
          return
        }

        repCounter?.update(state.index)
        const time = performance.now()
        setStateChartData(prev => {
          if (prev.length == 0 || state.index !== prev[0].state) {// || time - prev[0].time > 1000) {
            return [{ state: state.index, time }, ...prev.slice(0, 20)]
          }

          return prev
        })
      }
    } else {
      // TODO: unify these?
      setPoseData(null)
    }
  }, [currentExercise, repCounter, motion])

  useEffect(() => {
    MediapipeProcessor.onResults(onPoseResults)
  }, [onPoseResults])

  useEffect(() => {
    MediapipeProcessor.initialize().finally(() => {
      setTimeout(() => setIsInitialized(true), 3000) // TODO: remove this timeout
    })

    return () => {
      MediapipeProcessor.stopProcessing()
    }
  }, [setIsInitialized])

  // mediapipe processor
  useEffect(() => {
    if (!isMediaPipeActive) {
      MediapipeProcessor.stopProcessing()
      // setPoseData(null)

      return
    }

    if (videoElement) {
      setStateChartData([])
      MediapipeProcessor.startProcessing(videoElement)
    }
  }, [isMediaPipeActive, videoElement])

  useEffect(() => {
    MediapipeProcessor.enableEnhancedHands = settings.enableEnhancedHands

    if (!settings.enableEnhancedHands) {
      setHandsResults(null)
    }
  }, [settings.enableEnhancedHands])

  const stopCamera = useCallback(() => {
    camera.stopCamera()
    setVideoSrc(null)
    setPoseData(null)
  }, [camera])

  const startCamera = useCallback(async () => {
    setVideoSrc(null)
    setPoseData(null)

    try {
      // TODO: should this set facing mode to 'environment' for back camera?
      // ALSO TODO: should this use `resolution`, or should it always specify width > height?
      // const stream = await camera.startCamera({ width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' })
      const stream = await camera.startCamera({
        // NOTE: apparently iphone will set the correct orientation
        // so even though this is supposedly landscape, it will be flipped if phone is in portrait orientation
        width: { ideal: settings.preferredResolution?.width || 640 },
        height: { ideal: settings.preferredResolution?.width || 480 },
        // aspectRatio: ((settings.preferredResolution?.width || 640) / (settings.preferredResolution?.height || 480)), // resolution.width / resolution.height,
        facingMode: 'user',
        deviceId: settings.selectedCameraId || camera.availableCameras[0].deviceId,
      })
      setVideoSrc(stream)
    } catch (error) {
      console.error('Failed to start camera:', error)
    }
  }, [camera, settings.preferredResolution, settings.selectedCameraId])

  const stopReplay = useCallback(() => {
    setIsReplayMode(false)
    setVideoSrc(null)
    setPoseData(null)
  }, [setVideoSrc])

  const startReplay = useCallback(() => {
    // Start replay mode
    if (!currentExercise) {
      setVideoSrc(null)

      return
    }

    const videoPath = poseFilenameToVideoPath(currentExercise.name)

    // videoPath = '/male-Bodyweight-assisted-bulgarian-split-squat-front.mp4'
    // videoPath = '/cardio1.mp4'
    // videoPath = '/t2.mp4'
    // if (videoElement)
    //   videoElement.playbackRate = 0.5

    setVideoSrc(videoPath)
    setIsReplayMode(true)
  }, [currentExercise])

  const handleCameraStartStop = useCallback(async () => {
    // Also stop replay mode if it's running
    if (isReplayMode) {
      stopReplay()
    }

    if (camera.isCameraRunning) {
      stopCamera()
    } else {
      startCamera()
    }
  }, [camera.isCameraRunning, isReplayMode, stopCamera, stopReplay, startCamera])

  // Replay event handler
  const handleReplayStartStop = useCallback(() => {
    if (camera.isCameraRunning) {
      stopCamera()
    }

    if (isReplayMode) {
      stopReplay()
    } else {
      startReplay()
    }
  }, [camera.isCameraRunning, isReplayMode, stopCamera, stopReplay, startReplay])

  useEffect(() => {
    if (isReplayMode && currentExercise) {
      startReplay()
    }
  }, [currentExercise, isReplayMode, startReplay])

  return (
    <div className="App">
      {!isInitialized && (
        // TODO: loading bar while initializing
        <h1 style={{ color: 'white', zIndex: 15, position: 'absolute', top: '20px', left: '20px' }}>Initializing...</h1>
      )}

      <div style={{ display: 'flex', flexDirection: 'row', height: '100vh' }}>
        <SettingsArea availableCameras={camera.availableCameras} />

        {/* TODO: reuse this area for general info display, such as reps, etc. */}
        {isOutOfFrame && poseData && (
          <div style={{
            position: 'absolute',
            top: '50px',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'white',
            fontSize: '24px',
            fontWeight: 'bold',
            textAlign: 'center',
            backgroundColor: 'rgba(255,0,0,0.8)',
            padding: '12px',
            borderRadius: '10px',
            zIndex: 10000,
          }}>
              Get in frame
          </div>
        )}

        <VideoDisplay
          showVideo={settings.display_showVideo}
          videoSrc={videoSrc as string}
          onVideoElementRef={setVideoElement}
        />

        {/* Canvas for displaying camera feed stick figure */}
        <PoseCanvas
          currentKeyposeIdx={currentKeyposeIndex}
          width={videoElement?.videoWidth || 0}
          height={videoElement?.videoHeight || 0}
          poseData={poseData}
          handsResults={handsResults}
        />

        <PoseCanvas3D
          // width={videoElement?.videoWidth || 1}
          // height={videoElement?.videoHeight || 1}
          poseData={poseData}
          handsResults={handsResults}
        />

        <div style={{
          margin: '12px',
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          zIndex: 10000,

          top: 0,
          right: 0,

          // top: 0,
          // bottom: 0,
          // left: '50%',
          // transform: 'translateX(-50%)',
        }}>
          {/* Replay Button */}
          <button
            onClick={handleReplayStartStop}
            style={{
              ...buttonStyle,
              backgroundColor: isReplayMode ? '#dc3545' : 'orange',
            }}
          >
            🔄
          </button>

          {/* Start/Stop Button */}
          <button
            onClick={handleCameraStartStop}
            style={{
              ...buttonStyle,
              backgroundColor: camera.isCameraRunning ? '#dc3545' : '#28a745',
            }}
          >
            {camera.isCameraRunning ? '⏹️' : '▶️'}
          </button>
        </div>

        {settings.debug_showJointBars && poseData && (
          // Pose Display - Joint bars and exercise state
          <div
            style={{
              position: 'absolute',
              top: '36px',
              left: '10px',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <JointBars jointAngles={poseData.jointAngles} />
          </div>
        )}

        <div style={{ position: 'absolute', bottom: '10px', left: '10px', zIndex: 1000, pointerEvents: 'none' }}>
          {/* Exercise state and keypose bars */}
          <ExerciseBars
            exercise={currentExercise}
            currentPose={poseData}
            currentState={currentKeyposeIndex}
            repCount={repCount}
            dualSided={currentExercise?.isDualSided()}
            bestScoreThreshold={bestScoreThreshold}
          />
        </div>

        {/* TODO: this should be own element */}
        {/* Right side - Exercise canvas */}
        <div style={{ width: '250px', overflow: 'auto', position: 'absolute', bottom: '10px', right: '10px', zIndex: 10, backgroundColor: '#00000080', borderRadius: '10px', userSelect: 'none' }}>

          {/* TODO: remove the mediapipe resolution debug state */}
          {/* <p style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 1000, color: 'white' }}>{mpRes}</p> */}
          {/* TODO: display more info like canvas, video and camera sizes, etc. */}
          {settings.debug_showStatus && (
            <StatusDisplay
              fps={fps}
              resolution={videoElement?.videoWidth && videoElement?.videoHeight ? { width: videoElement.videoWidth, height: videoElement.videoHeight } : resolution}
            />
          )}

          {/* TODO: should the chart also be collapsible? */}
          {settings.debug_showGraph && (
            <LineChart width={230} height={100} data={stateChartData} style={{ padding: '10px', pointerEvents: 'none' }}>
              <Line dataKey="state" stroke="#8884d8" strokeWidth={2} type="monotone" isAnimationActive={false} />
            </LineChart>
          )}

          {settings.preview_showVideo && currentExercise && (
            <video src={poseFilenameToVideoPath(currentExercise.name, 'female')} muted autoPlay loop style={{ width: '100%', height: '100%', transform: 'scaleX(-1)' }} />
          )}

          <ExerciseCanvas
            currentKeyposeIndex={currentKeyposeIndex}
            width={640}
            height={480}
          />

        </div>
      </div>
    </div>
  )
}

function App () {
  return (
    <SettingsProvider>
      <ExerciseProvider>
        <AppContent />
      </ExerciseProvider>
    </SettingsProvider>
  )
}

export default App
